'use client';

import clsx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { MouseEventHandler, ReactNode } from 'react';

import { useSidebar } from '@/components/elements/Sidebar/sidebar-provider';
import { useIsMobile } from '@/hooks/useMediaQuery';

export interface ISidebarItem {
  icon: ReactNode;
  name: string;
  href?: string;
  onClick?: () => void;
  minimized?: boolean;
  visible?: boolean;
  active?: boolean;
  counter?: number;
  render?: () => React.ReactNode;
  matchMode?: 'auto' | 'exact' | 'startsWith';
}

const normalizeHref = (href: string) => (href?.startsWith('/') ? href : `/${href}`);

const isActivePath = (
  pathname: string,
  href: string,
  mode: 'auto' | 'exact' | 'startsWith' = 'auto',
) => {
  const nHref = normalizeHref(href);
  const nPath = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname;
  const base = nHref.endsWith('/') && nHref !== '/' ? nHref.slice(0, -1) : nHref;

  if (mode === 'exact') return nPath === base;

  return nPath === base || nPath.startsWith(base + '/');
};

export const SidebarItem = ({
  icon,
  name,
  href = '',
  minimized,
  active,
  matchMode,
}: ISidebarItem) => {
  const pathname = usePathname();
  const { setIsCollapsed } = useSidebar();
  const isMobile = useIsMobile();

  const handleClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (isMobile) {
      setIsCollapsed(true);
    }
  };
  const computedActive =
    typeof active === 'boolean' ? active : href ? isActivePath(pathname, href, matchMode) : false;
  const linkHref = href ? normalizeHref(href) : '#';

  return (
    <Link
      href={linkHref}
      onClick={handleClick}
      className={clsx(
        'group flex h-10 w-full items-center gap-x-3 overflow-hidden rounded-lg px-2 text-sm leading-5 font-medium whitespace-nowrap md:px-3',
        computedActive ? 'bg-primary' : 'text-cm-gray-700 hover:bg-cm-gray-100',
      )}
    >
      <>
        <span
          className={clsx(
            'relative flex h-6 w-6 shrink-0 items-center justify-center text-xl',
            computedActive ? 'text-text-primary' : 'text-white',
          )}
        >
          {icon}
        </span>
        <span
          className={clsx(
            minimized && 'pointer-events-none w-0 opacity-0 transition-all',
            computedActive ? 'text-text-primary' : 'text-white',
          )}
        >
          {name}
        </span>
      </>
    </Link>
  );
};
